import { useState, useEffect } from "react"
import { isEqual } from "lodash-es"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { getBookingResourceWithTableSetup } from "../form/helpers"
import {
    CreateUpdateBookingAttendee,
    CreateUpdateBookingResourceTableSetups,
} from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { NullableCalendarEvent } from "./calendar/fullCalendarTypes"
import { CreateOrderDialogFlow, CreateOrderFlowType, OrganizerSelectedValue } from "../../createOrderTypes"
import { getOrderPerson } from "../../../../utils/getOrderPerson"
import { defaultInitialEventState } from "../../defaults"

export type InitialValuesState = {
    id: number | null
    meetingName: string
    organizers: OrganizerSelectedValue
    attendees: CreateUpdateBookingAttendee[]
    resources: CreateUpdateBookingResourceTableSetups[]
    messageToServiceCenter?: string
    isPrivate?: boolean
    isExternal?: boolean
}

export type InitialEventState = {
    event: NullableCalendarEvent
    initValues: InitialValuesState | null
    isUpdated: boolean
}

export const useInitialEventState = (
    localEvent: NullableCalendarEvent,
    setLocalEvent: (event: NullableCalendarEvent) => void,
    orderDetails?: MeetingServiceOrder,
    flow?: CreateOrderDialogFlow["flow"],
) => {
    const [initialEventState, setInitialEventState] = useState<InitialEventState>(defaultInitialEventState)
    const isOutlookFlow = flow === CreateOrderFlowType.OUTLOOK

    useEffect(() => {
        if (initialEventState.initValues && localEvent && !isEqual(localEvent, initialEventState.event)) {
            setInitialEventState((prevState) => ({
                ...prevState,
                isUpdated: true,
            }))
        }
    }, [localEvent, initialEventState.event, initialEventState.initValues?.id])

    useEffect(() => {
        if (orderDetails) {
            const { id, meetingDetails, bookingDetails, messageToServiceCenter } = orderDetails
            const { person } = getOrderPerson(orderDetails.organizers)

            setInitialEventState({
                event: localEvent,
                initValues: {
                    id,
                    meetingName: meetingDetails.meetingName || "",
                    organizers: person!,
                    attendees: meetingDetails.attendees || [],
                    resources: bookingDetails?.resources
                        ? getBookingResourceWithTableSetup(bookingDetails.resources)
                        : [],
                    messageToServiceCenter: messageToServiceCenter ?? "",
                    isPrivate: meetingDetails.isPrivate || false,
                    isExternal: meetingDetails.isExternal || false,
                },
                isUpdated: false,
            })
        }
    }, [orderDetails?.id])

    const handleCancelClick = () => {
        const shouldResetToInitialEvent =
            initialEventState.initValues?.id || (!initialEventState.initValues?.id && isOutlookFlow)

        if (shouldResetToInitialEvent) {
            setLocalEvent(initialEventState.event)
            setInitialEventState((prevState) => ({
                ...prevState,
                isUpdated: false,
            }))
        } else {
            setLocalEvent(null)
        }
    }

    return {
        initialEventState,
        setInitialEventState,
        handleCancelClick,
    }
}
